/**
 * useSkillCatalogue
 *
 * Fetches the full skill catalogue from the server once per session and caches it.
 * Returns skills in the SkillItem shape expected by SkillCard / SkillLibrarySidebar.
 *
 * Falls back to an empty array if the fetch fails so the UI degrades gracefully.
 */

import { useState, useEffect } from 'react';
import { type SkillItem } from '../components/agentWorkspace/workspaceData.ts';

// ── Module-level cache so multiple consumers share a single fetch ─────────────
let _cache: SkillItem[] | null = null;
let _pending: Promise<SkillItem[]> | null = null;

/** Map a raw catalogue object from the API to the SkillItem shape. */
function toSkillItem(s: any): SkillItem {
  const domain = s.domain === 'sdlc' ? 'coding' : (s.domain || 'research');
  return {
    id:          s.id,
    domain,
    icon:        s.icon     || 'Package',
    name:        s.name,
    description: s.description || '',
    tags:        Array.isArray(s.tags) ? s.tags : [],
    uses:        s.uses     || '0',
    official:    !!s.official,
    prompt:      s.prompt   || undefined,
    source:      s.source   || 'catalogue',
    lyraSkill:   s.lyraSkill ? true : undefined,
    templateCount: Number(s.templateCount) || 0,
  };
}

function fetchCatalogue(): Promise<SkillItem[]> {
  if (_pending !== null) return _pending;

  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const base = (process.env.REACT_APP_API_URL as string) || '';
  _pending = Promise.all([
    fetch(`${base}/api/skills/catalogue`, { headers }).then(r => r.ok ? r.json() : { skills: [] }),
    // This is the same registry Home's +Tools picker uses. Keeping it here
    // makes Agent Builder, its modal, and the library sidebar agree with Home.
    fetch(`${base}/api/skills-registry`, { headers }).then(r => r.ok ? r.json() : { skills: [] }).catch(() => ({ skills: [] })),
  ])
    .then(([catalogue, registry]: any[]) => {
      const items = (catalogue.skills || []).map(toSkillItem);
      const known = new Set(items.map((item: SkillItem) => item.id));
      for (const skill of registry.skills || []) {
        if (!known.has(skill.id)) items.push(toSkillItem({ ...skill, official: true, lyraSkill: true, source: 'lyraskills' }));
      }
      _cache = items;
      return items as SkillItem[];
    })
    .catch((): SkillItem[] => {
      _pending = null; // allow retry on next mount
      return [];
    });

  return _pending;
}

/** Invalidate the module cache (call after user uploads/installs a skill). */
export function invalidateSkillCatalogue() {
  _cache = null;
  _pending = null;
}

export function useSkillCatalogue() {
  const [skills, setSkills] = useState<SkillItem[]>(_cache ?? []);
  const [loading, setLoading] = useState<boolean>(!_cache);

  useEffect(() => {
    if (_cache) {
      setSkills(_cache);
      setLoading(false);
      return;
    }

    fetchCatalogue().then(items => {
      setSkills(items);
      setLoading(false);
    });
  }, []);

  return { skills, loading };
}
